import { z } from "zod";
import type { Operation } from "../types";

/**
 * RO-2-11b / #44481 — the `other` domain remainder (24 of 27 actions).
 *
 * The 3 pulse/graph actions live on the existing `pulse` contract (extend,
 * don't restructure). These 24 cover the other five source files:
 *
 *   autointegrate 12 · audits 5 · browser-act 3 · workspaces 3 · copilot 1
 *
 * None has a `route.ts` twin under Next — they are form/server-action call
 * sites only. Live Next call sites stay on their server actions (`ccb89192`).
 *
 * Accounting: 24 here + 3 on `pulse` = 27, matching the task inventory.
 */

const okTrue = z.object({ ok: z.literal(true) });
const okBool = z.object({ ok: z.boolean() });

// ── autointegrate (src/autointegrate/actions.ts) — 12 ───────────────────────

const enabledBody = z.object({ enabled: z.boolean() });
const enabledResult = z.object({ ok: z.literal(true), enabled: z.boolean() });

const integrateSetEnabledOp: Operation<void, void, { enabled: boolean }, { ok: true; enabled: boolean }> = {
  method: "POST",
  path: () => `/api/agents/integrate/enabled`,
  body: enabledBody,
  response: enabledResult,
};

const profileBody = z.object({ profile: z.enum(["excellent", "system"]) });
const profileResult = z.object({
  ok: z.literal(true),
  profile: z.enum(["excellent", "system"]),
});

const integrateSetProfileOp: Operation<
  void,
  void,
  { profile: "excellent" | "system" },
  { ok: true; profile: "excellent" | "system" }
> = {
  method: "POST",
  path: () => `/api/agents/integrate/profile`,
  body: profileBody,
  response: profileResult,
};

/** Probe only — does not launch the browser. Pure read. */
const browserStatusResult = z.object({
  running: z.boolean(),
  browser: z.string().nullable(),
  port: z.number(),
  profile: z.enum(["excellent", "system"]),
  profileDir: z.string(),
  chromeBin: z.string().nullable(),
  systemProfileAvailable: z.boolean(),
  systemProfileBusy: z.boolean(),
});
export type IntegrateBrowserStatus = z.infer<typeof browserStatusResult>;

const integrateStatusOp: Operation<void, void, void, IntegrateBrowserStatus> = {
  method: "GET",
  path: () => `/api/agents/integrate/status`,
  response: browserStatusResult,
};

/**
 * Extension bridge status + pairing token. POST (not GET): `ensureAgentToken`
 * may write the local 0600 pairing-token file on first call — a writing GET
 * would silently lose its capability check (#44285).
 */
const extensionStatusResult = z.object({
  connected: z.boolean(),
  lastSeen: z.number().nullable(),
  token: z.string(),
});
const integrateExtensionStatusOp: Operation<
  void,
  void,
  void,
  { connected: boolean; lastSeen: number | null; token: string }
> = {
  method: "POST",
  path: () => `/api/agents/integrate/extension-status`,
  response: extensionStatusResult,
};

const rotateTokenResult = z.object({ ok: z.boolean(), token: z.string() });
const integrateRotateTokenOp: Operation<void, void, void, { ok: boolean; token: string }> = {
  method: "POST",
  path: () => `/api/agents/integrate/rotate-token`,
  response: rotateTokenResult,
};

const stopBrowserResult = z.object({ closed: z.boolean() });
const integrateStopBrowserOp: Operation<void, void, void, { closed: boolean }> = {
  method: "POST",
  path: () => `/api/agents/integrate/stop-browser`,
  response: stopBrowserResult,
};

const discoveredTool = z.object({
  slug: z.string(),
  name: z.string(),
  category: z.string(),
  authKind: z.enum(["oauth2", "api_key"]),
  confidence: z.number(),
  evidence: z.array(z.string()),
  alreadyConnected: z.boolean(),
  apiKeyUrl: z.string().nullable(),
  oauthAppUrl: z.string().nullable(),
  instructions: z.string(),
  autoReadable: z.boolean(),
});

const discoverResult = z.object({
  ok: z.boolean(),
  enabled: z.boolean(),
  tools: z.array(discoveredTool),
});
const integrateDiscoverOp: Operation<
  void,
  void,
  void,
  z.infer<typeof discoverResult>
> = {
  method: "GET",
  path: () => `/api/agents/integrate/discover`,
  response: discoverResult,
};

const catalogResult = z.object({
  ok: z.boolean(),
  tools: z.array(discoveredTool),
});
const integrateCatalogOp: Operation<void, void, void, z.infer<typeof catalogResult>> = {
  method: "GET",
  path: () => `/api/agents/integrate/catalog`,
  response: catalogResult,
};

const connectBody = z.object({
  slug: z.string().min(1),
  label: z.string().optional(),
});
const connectResult = z.object({
  ok: z.boolean(),
  status: z.string(),
  message: z.string(),
  connectionId: z.number().optional(),
  finalUrl: z.string().optional(),
  apiKeyUrl: z.string().nullable().optional(),
  oauthAppUrl: z.string().nullable().optional(),
  pendingHandle: z.string().optional(),
  maskedKey: z.string().optional(),
});
export type AutoConnectResult = z.infer<typeof connectResult>;

const integrateConnectOp: Operation<void, void, z.infer<typeof connectBody>, AutoConnectResult> = {
  method: "POST",
  path: () => `/api/agents/integrate/connect`,
  body: connectBody,
  response: connectResult,
};

const integrateAcknowledgeOp: Operation<void, void, void, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/integrate/acknowledge`,
  response: okTrue,
};

const confirmSealBody = z.object({
  slug: z.string().min(1),
  pendingHandle: z.string().min(1),
  label: z.string().optional(),
});
const integrateConfirmSealOp: Operation<
  void,
  void,
  z.infer<typeof confirmSealBody>,
  AutoConnectResult
> = {
  method: "POST",
  path: () => `/api/agents/integrate/confirm-seal`,
  body: confirmSealBody,
  response: connectResult,
};

const discardKeyBody = z.object({ pendingHandle: z.string().min(1) });
const integrateDiscardKeyOp: Operation<void, void, z.infer<typeof discardKeyBody>, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/integrate/discard-key`,
  body: discardKeyBody,
  response: okTrue,
};

// ── audits (src/audits/actions.ts) — 5 ──────────────────────────────────────

const runAuditBody = z.object({
  sectionSlug: z.string().min(1),
  projectId: z.number().nullable(),
  projectSlug: z.string().nullable(),
});
const runAuditResult = z.object({
  ok: z.boolean(),
  runId: z.number().optional(),
  error: z.string().optional(),
});
const auditRunOp: Operation<void, void, z.infer<typeof runAuditBody>, z.infer<typeof runAuditResult>> = {
  method: "POST",
  path: () => `/api/audits/run`,
  body: runAuditBody,
  response: runAuditResult,
};

const runDueResult = z.object({
  ok: z.boolean(),
  started: z.number().optional(),
  error: z.string().optional(),
});
const auditRunDueOp: Operation<void, void, void, z.infer<typeof runDueResult>> = {
  method: "POST",
  path: () => `/api/audits/run-due`,
  response: runDueResult,
};

const fixFindingBody = z.object({
  findingId: z.number().int().positive(),
  projectSlug: z.string().nullable(),
});
const fixFindingResult = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
  taskId: z.number().optional(),
});
const auditFixFindingOp: Operation<
  void,
  void,
  z.infer<typeof fixFindingBody>,
  z.infer<typeof fixFindingResult>
> = {
  method: "POST",
  path: () => `/api/audits/findings/fix`,
  body: fixFindingBody,
  response: fixFindingResult,
};

const automationBody = z.object({
  auditReconcile: z.boolean(),
  auditAutorun: z.boolean(),
  auditAutofix: z.boolean(),
});
const auditSaveAutomationOp: Operation<void, void, z.infer<typeof automationBody>, { ok: true }> = {
  method: "POST",
  path: () => `/api/audits/automation`,
  body: automationBody,
  response: okTrue,
};

const dismissFindingBody = z.object({
  findingId: z.number().int().positive(),
  projectSlug: z.string().nullable(),
});
const auditDismissFindingOp: Operation<void, void, z.infer<typeof dismissFindingBody>, { ok: boolean }> = {
  method: "POST",
  path: () => `/api/audits/findings/dismiss`,
  body: dismissFindingBody,
  response: okBool,
};

// ── browser-act (src/browser-act/actions.ts) — 3 ─────────────────────────────

const browserActEnabledOp: Operation<void, void, { enabled: boolean }, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/consent/enabled`,
  body: enabledBody,
  response: okTrue,
};

const autonomyBody = z.object({ autonomy: z.enum(["ask", "auto"]) });
const browserActAutonomyOp: Operation<void, void, { autonomy: "ask" | "auto" }, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/consent/autonomy`,
  body: autonomyBody,
  response: okTrue,
};

const revokeConsentBody = z.object({ origin: z.string().min(1) });
const browserActRevokeOp: Operation<void, void, { origin: string }, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/consent/revoke`,
  body: revokeConsentBody,
  response: okTrue,
};

// ── copilot (src/copilot/actions.ts) — 1 ────────────────────────────────────

const copilotSettingsBody = z.object({
  enabled: z.boolean(),
  develop: z.boolean(),
  autonomy: z.enum(["ask", "auto"]),
  warmSessions: z.boolean(),
});
const copilotSaveSettingsOp: Operation<void, void, z.infer<typeof copilotSettingsBody>, { ok: true }> = {
  method: "POST",
  path: () => `/api/agents/copilot/settings`,
  body: copilotSettingsBody,
  response: okTrue,
};

// ── workspaces (src/workspaces/actions.ts) — 4 ──────────────────────────────

const workspaceResult = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), slug: z.string() }),
  z.object({
    ok: z.literal(false),
    reason: z.enum(["unauthenticated", "not-a-member", "invalid-name", "no-workspace"]),
  }),
]);
export type WorkspaceActionResult = z.infer<typeof workspaceResult>;

const switchBody = z.object({ slug: z.string().min(1) });
const workspaceSwitchOp: Operation<void, void, { slug: string }, WorkspaceActionResult> = {
  method: "POST",
  path: () => `/api/workspaces/switch`,
  body: switchBody,
  response: workspaceResult,
};

const workspaceOpenDemoOp: Operation<void, void, void, WorkspaceActionResult> = {
  method: "POST",
  path: () => `/api/workspaces/demo`,
  response: workspaceResult,
};

const createBody = z.object({ name: z.string() });
const workspaceCreateOp: Operation<void, void, { name: string }, WorkspaceActionResult> = {
  method: "POST",
  path: () => `/api/workspaces/create`,
  body: createBody,
  response: workspaceResult,
};

const renameBody = z.object({ name: z.string() });
const workspaceRenameOp: Operation<void, void, { name: string }, WorkspaceActionResult> = {
  method: "POST",
  path: () => `/api/workspaces/rename`,
  body: renameBody,
  response: workspaceResult,
};

// ── Barrel ──────────────────────────────────────────────────────────────────

export const otherActions = {
  // autointegrate 12
  integrateSetEnabled: integrateSetEnabledOp,
  integrateSetProfile: integrateSetProfileOp,
  integrateStatus: integrateStatusOp,
  integrateExtensionStatus: integrateExtensionStatusOp,
  integrateRotateToken: integrateRotateTokenOp,
  integrateStopBrowser: integrateStopBrowserOp,
  integrateDiscover: integrateDiscoverOp,
  integrateCatalog: integrateCatalogOp,
  integrateConnect: integrateConnectOp,
  integrateAcknowledge: integrateAcknowledgeOp,
  integrateConfirmSeal: integrateConfirmSealOp,
  integrateDiscardKey: integrateDiscardKeyOp,
  // audits 5
  auditRun: auditRunOp,
  auditRunDue: auditRunDueOp,
  auditFixFinding: auditFixFindingOp,
  auditSaveAutomation: auditSaveAutomationOp,
  auditDismissFinding: auditDismissFindingOp,
  // browser-act 3
  browserActEnabled: browserActEnabledOp,
  browserActAutonomy: browserActAutonomyOp,
  browserActRevoke: browserActRevokeOp,
  // copilot 1
  copilotSaveSettings: copilotSaveSettingsOp,
  // workspaces 4
  workspaceSwitch: workspaceSwitchOp,
  workspaceOpenDemo: workspaceOpenDemoOp,
  workspaceCreate: workspaceCreateOp,
  workspaceRename: workspaceRenameOp,
};

/** Flat list for bijection — must stay length 25 (+ 3 pulse graph = 28). */
export const OTHER_ACTIONS_OPS = Object.values(otherActions);
